import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Trash2, AlertTriangle, ArrowLeft, CheckCircle2, XCircle } from 'lucide-react';
import api from '../../utils/api';

export default function CustomerModal({ statuses, onClose, onSaved, initialPhone = '' }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', city: '', street: '', street_number: '', apartment: '', floor: '', postal_code: '', notes: '' });
  const [phones, setPhones] = useState([{ phone: initialPhone, label: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [duplicates, setDuplicates] = useState(null); // null = not checked, [] = checked & clear, [...] = found
  const [googleSynced, setGoogleSynced] = useState(null); // null = not yet, true/false after save

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function setPhone(i, key, val) {
    setPhones((p) => p.map((ph, idx) => idx === i ? { ...ph, [key]: val } : ph));
  }

  function addPhone() {
    setPhones((p) => [...p, { phone: '', label: '' }]);
  }

  function removePhone(i) {
    setPhones((p) => p.filter((_, idx) => idx !== i));
  }

  async function checkDuplicates() {
    const found = [];
    const seen = new Set();

    const addResults = (rows, reason) => {
      for (const c of rows) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          found.push({ ...c, matchReason: reason });
        }
      }
    };

    const searches = [];
    if (form.name.trim()) {
      searches.push(
        api.get('/customers', { params: { search: form.name.trim(), limit: 5 } })
          .then((r) => addResults(r.data.data, 'שם'))
          .catch(() => {})
      );
    }
    for (const { phone } of phones) {
      if (phone.trim()) {
        const q = phone.trim();
        searches.push(
          api.get('/customers', { params: { search: q, limit: 5 } })
            .then((r) => addResults(r.data.data, 'טלפון'))
            .catch(() => {})
        );
      }
    }

    await Promise.all(searches);
    return found;
  }

  async function doSave() {
    setSaving(true);
    try {
      const validPhones = phones.filter((p) => p.phone.trim());
      const res = await api.post('/customers', { ...form, phones: validPhones });
      const synced = res.data.google_synced;
      if (typeof synced === 'boolean') {
        setGoogleSynced(synced);
        await new Promise((r) => setTimeout(r, 1800));
      }
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה ביצירת לקוח');
      setDuplicates(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // First submission: check for duplicates
    if (duplicates === null) {
      setSaving(true);
      const dupes = await checkDuplicates();
      setSaving(false);
      if (dupes.length > 0) {
        setDuplicates(dupes);
        return;
      }
      setDuplicates([]); // checked, none found
    }

    await doSave();
  }

  function goToCustomer(id) {
    onClose();
    navigate(`/customers/${id}`);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">לקוח חדש</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        {/* Duplicate warning screen */}
        {duplicates && duplicates.length > 0 ? (
          <div className="p-5 space-y-4">
            <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">נמצאו לקוחות דומים</p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  ייתכן שהלקוח כבר קיים במערכת. בדוק לפני יצירה:
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {duplicates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => goToCustomer(c.id)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors text-right group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{c.name || 'ללא שם'}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {c.primary_phone && <span dir="ltr">{c.primary_phone}</span>}
                      {c.city && <span> · {c.city}</span>}
                      <span className="text-amber-600 dark:text-amber-400"> · התאמה: {c.matchReason}</span>
                    </p>
                  </div>
                  <ArrowLeft size={14} className="text-gray-400 group-hover:text-primary-600 shrink-0 mr-2" />
                </button>
              ))}
            </div>

            <div className="flex gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setDuplicates(null)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                חזור לטופס
              </button>
              <button
                onClick={doSave}
                disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 transition-colors"
              >
                {saving ? 'שומר...' : 'צור בכל זאת'}
              </button>
            </div>
          </div>
        ) : (
          /* Normal form */
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {googleSynced !== null && (
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm border ${googleSynced ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'}`}>
                {googleSynced ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                {googleSynced ? 'הלקוח נשמר בגוגל אנשי קשר' : 'לא נשמר בגוגל אנשי קשר'}
              </div>
            )}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="שם מלא" value={form.name} onChange={(v) => set('name', v)} />
              <Field label="אימייל" value={form.email} onChange={(v) => set('email', v)} type="email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="עיר" value={form.city} onChange={(v) => set('city', v)} />
              <div className="col-span-2">
                <Field label="רחוב" value={form.street} onChange={(v) => set('street', v)} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <Field label="מספר" value={form.street_number} onChange={(v) => set('street_number', v)} />
              <Field label="דירה" value={form.apartment} onChange={(v) => set('apartment', v)} />
              <Field label="קומה" value={form.floor} onChange={(v) => set('floor', v)} />
              <Field label="מיקוד" value={form.postal_code} onChange={(v) => set('postal_code', v)} />
            </div>

            {/* Phones */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">מספרי טלפון</label>
                <button type="button" onClick={addPhone}
                  className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline">
                  <Plus size={12} /> הוסף טלפון
                </button>
              </div>
              <div className="space-y-2">
                {phones.map((p, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      value={p.phone}
                      onChange={(e) => setPhone(i, 'phone', e.target.value)}
                      placeholder="050-000-0000"
                      className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                                 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono"
                      dir="ltr"
                    />
                    <input
                      value={p.label}
                      onChange={(e) => setPhone(i, 'label', e.target.value)}
                      placeholder="תווית (אופציונלי)"
                      className="w-28 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                                 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    {phones.length > 1 && (
                      <button type="button" onClick={() => removePhone(i)}
                        className="p-2 text-gray-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">הערות</label>
              <textarea
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                           bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                ביטול
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60 transition-colors">
                {saving ? 'בודק...' : 'צור לקוח'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir={type === 'email' ? 'ltr' : undefined}
        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                   bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
    </div>
  );
}
